import { createContext, useContext, useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import AuthenticationContext from 'config/context/authenticationContext';
import GuiContext from 'config/context/guiContext';

const CompositionsContext = createContext();

export const CompositionsContextProvider = ({ children }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { getCompositionPermission, permissionsData } = useContext(AuthenticationContext);
    const { selected: { selectedAccount } } = useContext(GuiContext);

    // Core states
    const [databaseStructure, setDatabaseStructure] = useState({});
    const [databaseScopes, setDatabaseScopes] = useState();
    const [selectedComposition, setSelectedComposition] = useState(initialSelectedComposition);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [tempPrivateKey, setTempPrivateKey] = useState('');
    const [editedRowsData, setEditedRowsData] = useState({});

    // Centralized loading: track multiple concurrent operations
    const [activeLoaders, setActiveLoaders] = useState(new Set());
    const startLoading = (key) => {
        setActiveLoaders(prev => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });
    };
    const stopLoading = (key) => {
        setActiveLoaders(prev => {
            const next = new Set(prev);
            next.delete(key);
            return next;
        });
    };
    const loading = activeLoaders.size > 0;

    // Simple route check
    const isCompositionsRoute = location.pathname === '/compositions';
    // Simplified composition selection
    const selectComposition = (compositionName) => {
        const composition = databaseStructure.compositions?.find(comp => comp.name === compositionName);
        if (composition) {
            // Check if composition is actually changing
            const isCompositionChanging = selectedComposition.name !== composition.name;
            
            if (isCompositionChanging) {
                // Clear selected rows and temp private key when changing composition
                setSelectedRows(new Set());
                setTempPrivateKey('');
                setEditedRowsData({});
                
                // Set completely new composition data
                const scopeFromUrl = searchParams.get('scope');
                const scopeToUse = scopeFromUrl || selectedAccount || '';
                
                setSelectedComposition({
                    name: composition.name,
                    meta_name: composition.meta_name,
                    permission: getCompositionPermission(composition.name),
                    description: composition.description,
                    count: 0,
                    selected_scope: scopeToUse,
                    structure: {
                        indexes: composition.indexes || [],
                        fields: composition.fields || []
                    },
                    rows: []
                });
            } else {
                // Only update permission, preserve all other existing data
                setSelectedComposition(prev => ({
                    ...prev,
                    permission: getCompositionPermission(composition.name)
                }));
            }

            // URL update with composition and scope (preserve scope or set default)
            if (isCompositionsRoute) {
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.set('composition', compositionName);
                const scopeFromUrl = searchParams.get('scope');
                const scopeToUse = scopeFromUrl || selectedComposition?.selected_scope || selectedAccount || '';
                if (scopeToUse) newSearchParams.set('scope', scopeToUse);
                setSearchParams(newSearchParams);
            }
        }
    };

    // Single useEffect for composition auto-selection
    useEffect(() => {
        if (databaseStructure?.compositions?.length > 0 && permissionsData) {
            const compositionFromUrl = searchParams.get('composition');
            const validComposition = compositionFromUrl &&
                databaseStructure.compositions.some(comp => comp.name === compositionFromUrl);

            const compositionName = validComposition ? compositionFromUrl : databaseStructure.compositions[0].name;

            // Use existing selectComposition function
            selectComposition(compositionName);
        }
    }, [databaseStructure, searchParams, isCompositionsRoute, permissionsData]);

    // Clear selection when leaving the compositions route
    useEffect(() => {
        if (location.pathname !== '/compositions') {
            setSelectedComposition(initialSelectedComposition);
            setSelectedRows(new Set());
            setTempPrivateKey('');
            setEditedRowsData({});
        }
    }, [location.pathname]);

    // Keep selected_scope in state and URL; enforce order: composition then scope
    useEffect(() => {
        if (!isCompositionsRoute) return;

        const existingComposition = searchParams.get('composition');
        const existingScope = searchParams.get('scope');

        const compositionParam = existingComposition || selectedComposition?.name || '';
        const scopeParam = existingScope || selectedComposition?.selected_scope || selectedAccount || '';

        // Sync state selected_scope from resolved scopeParam
        if (scopeParam && selectedComposition?.selected_scope !== scopeParam) {
            setSelectedComposition(prev => ({ ...prev, selected_scope: scopeParam }));
        }

        // Build desired ordered params
        const desired = new URLSearchParams();
        if (compositionParam) desired.set('composition', compositionParam);
        if (scopeParam) desired.set('scope', scopeParam);

        // Only update if different (including order)
        if (searchParams.toString() !== desired.toString()) {
            setSearchParams(desired);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCompositionsRoute, searchParams, selectedComposition?.name, selectedComposition?.selected_scope, selectedAccount]);

    useEffect(()=>{
        const scopeCount = selectedComposition?.scopes?.find(scope => scope.scope === selectedComposition?.selected_scope)?.count;
        setSelectedComposition(prev => ({
            ...prev,
            scope_count: scopeCount ? scopeCount : 0,
        }));
    }, [selectedComposition?.scopes, selectedComposition?.selected_scope])

    // Validate and reset scope if it doesn't exist in current composition
    // If something goes wrong with the scope selection in URL, remove useEffect block below
    useEffect(() => {
        if (!isCompositionsRoute || !selectedComposition?.name || !selectedComposition?.scopes?.length) {
            return;
        }

        const currentScope = selectedComposition?.selected_scope;
        const scopeExists = selectedComposition.scopes.some(scope => scope.scope === currentScope);

        // If current scope doesn't exist in composition scopes, reset to default
        if (currentScope && !scopeExists) {
            const defaultScope = selectedAccount || '';
            setSelectedComposition(prev => ({
                ...prev,
                selected_scope: defaultScope
            }));
            
            // Update URL to remove invalid scope
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('composition', selectedComposition.name);
            if (defaultScope) {
                newSearchParams.set('scope', defaultScope);
            } else {
                newSearchParams.delete('scope');
            }
            setSearchParams(newSearchParams);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedComposition?.name, selectedComposition?.scopes, isCompositionsRoute, selectedAccount])

    // Simple row selection functions
    const toggleRowSelection = (rowId) => {
        setSelectedRows(prev => {
            const newSet = new Set(prev);
            newSet.has(rowId) ? newSet.delete(rowId) : newSet.add(rowId);
            return newSet;
        });
    };

    // Edited rows helpers
    const setEditedFieldValue = (rowId, fieldName, value) => {
        setEditedRowsData(prev => ({
            ...prev,
            [rowId]: {
                ...prev[rowId],
                [fieldName]: value
            }
        }));
    };

    const clearEditedRows = () => setEditedRowsData({});

    // Scope selection
    const selectScope = (scope) => {
        setSelectedComposition(prev => ({
            ...prev,
            selected_scope: scope
        }));
        if (isCompositionsRoute) {
            // Enforce order: composition then scope
            const orderedParams = new URLSearchParams();
            if (selectedComposition?.name) {
                orderedParams.set('composition', selectedComposition.name);
            }
            orderedParams.set('scope', scope || selectedAccount || '');
            setSearchParams(orderedParams);
        }
    };

    const valueToShare = {
        // Core
        databaseStructure,
        setDatabaseStructure,
        databaseScopes,
        setDatabaseScopes,
        selectedComposition,
        setSelectedComposition,
        selectComposition,

        // Centralized loading
        loading,
        startLoading,
        stopLoading,

        // Row selection
        selectedRows: Array.from(selectedRows),
        toggleRowSelection,
        selectAllRows: (rowIds) => setSelectedRows(new Set(rowIds)),
        clearSelection: () => setSelectedRows(new Set()),
        isRowSelected: (rowId) => selectedRows.has(rowId),
        selectedCount: selectedRows.size,

        // Composition names
        compositionNames: databaseStructure?.compositions?.map(comp => comp.name) || [],

        // Auth
        tempPrivateKey,
        setTempPrivateKey,

        // Edited rows
        editedRowsData,
        setEditedRowsData,
        setEditedFieldValue,
        clearEditedRows,

        // Scope selection
        selectScope
    };

    return (
        <CompositionsContext.Provider value={valueToShare}>
            {children}
        </CompositionsContext.Provider>
    );
};

export const useCompositionsContext = () => {
    const context = useContext(CompositionsContext);
    if (!context) {
        throw new Error('useCompositionsContext must be used within CompositionsContextProvider');
    }
    return context;
};

// Initial values
const initialSelectedComposition = {
    name: '',
    meta_name: '',
    permission: '',
    description: '',
    count: 0,
    scope_count: 0,
    scopes: [],
    selected_scope: '',
    structure: {
        indexes: [],
        fields: []
    },
    rows: []
}